import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Scissors, RefreshCw, MessageSquare, FileText } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import AnimatedSection from '../components/AnimatedSection';
import { useLanguage } from '../i18n/pageTranslations';

const CirugiaReparadora = () => {
  const lang = useLanguage();

  const texts = {
    es: {
      eyebrow: 'Cirugía Reparadora y Pericial',
      title1: 'Cuando la cirugía',
      title2: 'es necesidad',
      description: 'Cirugía reconstructiva, corrección de secuelas y valoración pericial. Expertise en casos complejos que requieren experiencia y criterio clínico.',
      moreInfo: 'Más información',
      ctaEyebrow: 'Consulta de Valoración',
      ctaTitle: '¿Necesita una valoración?',
      ctaDesc: 'Consulta personalizada para analizar su caso y proponer las opciones más adecuadas.',
      ctaButton: 'Solicitar consulta',
    },
    en: {
      eyebrow: 'Reconstructive & Expert Surgery',
      title1: 'When surgery',
      title2: 'is necessary',
      description: 'Reconstructive surgery, sequelae correction and expert assessment. Expertise in complex cases requiring experience and clinical judgment.',
      moreInfo: 'More information',
      ctaEyebrow: 'Assessment Consultation',
      ctaTitle: 'Do you need an assessment?',
      ctaDesc: 'Personalized consultation to analyze your case and propose the most suitable options.',
      ctaButton: 'Request consultation',
    },
    fr: {
      eyebrow: 'Chirurgie Réparatrice et Expertale',
      title1: 'Quand la chirurgie',
      title2: 'est nécessaire',
      description: 'Chirurgie reconstructive, correction des séquelles et évaluation d\'expert. Expertise dans les cas complexes nécessitant expérience et jugement clinique.',
      moreInfo: 'Plus d\'informations',
      ctaEyebrow: 'Consultation d\'Évaluation',
      ctaTitle: 'Avez-vous besoin d\'une évaluation?',
      ctaDesc: 'Consultation personnalisée pour analyser votre cas et proposer les options les plus adaptées.',
      ctaButton: 'Demander une consultation',
    },
    pt: {
      eyebrow: 'Cirurgia Reparadora e Pericial',
      title1: 'Quando a cirurgia',
      title2: 'é necessária',
      description: 'Cirurgia reconstrutiva, correção de sequelas e avaliação pericial. Expertise em casos complexos que requerem experiência e critério clínico.',
      moreInfo: 'Mais informações',
      ctaEyebrow: 'Consulta de Avaliação',
      ctaTitle: 'Precisa de uma avaliação?',
      ctaDesc: 'Consulta personalizada para analisar seu caso e propor as opções mais adequadas.',
      ctaButton: 'Solicitar consulta',
    },
  };

  const procedimientos = {
    es: [
      { titulo: 'Cirugía Dermatológica', icono: Scissors, descripcion: 'Extirpación de lesiones cutáneas con técnica oncológica. Reconstrucción estética post-exéresis.', url: '/cirugia-dermatologica' },
      { titulo: 'Cirugía Plástica Secundaria', icono: RefreshCw, descripcion: 'Corrección de resultados previos insatisfactorios. Revisión de cirugías previas.', url: '/cirugias-secundarias' },
      { titulo: 'Segunda Opinión', icono: MessageSquare, descripcion: 'Valoración independiente antes de decidir. Análisis objetivo de opciones.', url: '/segunda-opinion' },
      { titulo: 'Peritación Médica y Daño Corporal', icono: FileText, descripcion: 'Informes periciales para procedimientos judiciales y extrajudiciales. Valoración del daño corporal.', url: '/peritacion-medica' },
    ],
    en: [
      { titulo: 'Dermatological Surgery', icono: Scissors, descripcion: 'Excision of skin lesions with oncological technique. Aesthetic reconstruction post-excision.', url: '/cirugia-dermatologica' },
      { titulo: 'Secondary Plastic Surgery', icono: RefreshCw, descripcion: 'Correction of unsatisfactory previous results. Revision of previous surgeries.', url: '/cirugias-secundarias' },
      { titulo: 'Second Opinion', icono: MessageSquare, descripcion: 'Independent assessment before deciding. Objective analysis of options.', url: '/segunda-opinion' },
      { titulo: 'Medical Expertise & Bodily Damage', icono: FileText, descripcion: 'Expert reports for judicial and extrajudicial proceedings. Bodily damage assessment.', url: '/peritacion-medica' },
    ],
    fr: [
      { titulo: 'Chirurgie Dermatologique', icono: Scissors, descripcion: 'Excision de lésions cutanées avec technique oncologique. Reconstruction esthétique post-exérèse.', url: '/cirugia-dermatologica' },
      { titulo: 'Chirurgie Plastique Secondaire', icono: RefreshCw, descripcion: 'Correction de résultats antérieurs insatisfaisants. Révision de chirurgies précédentes.', url: '/cirugias-secundarias' },
      { titulo: 'Deuxième Avis', icono: MessageSquare, descripcion: 'Évaluation indépendante avant de décider. Analyse objective des options.', url: '/segunda-opinion' },
      { titulo: 'Expertise Médicale et Dommage Corporel', icono: FileText, descripcion: 'Rapports d\'expertise pour procédures judiciaires et extrajudiciaires. Évaluation du dommage corporel.', url: '/peritacion-medica' },
    ],
    pt: [
      { titulo: 'Cirurgia Dermatológica', icono: Scissors, descripcion: 'Excisão de lesões cutâneas com técnica oncológica. Reconstrução estética pós-exérese.', url: '/cirugia-dermatologica' },
      { titulo: 'Cirurgia Plástica Secundária', icono: RefreshCw, descripcion: 'Correção de resultados anteriores insatisfatórios. Revisão de cirurgias prévias.', url: '/cirugias-secundarias' },
      { titulo: 'Segunda Opinião', icono: MessageSquare, descripcion: 'Avaliação independente antes de decidir. Análise objetiva de opções.', url: '/segunda-opinion' },
      { titulo: 'Perícia Médica e Dano Corporal', icono: FileText, descripcion: 'Relatórios periciais para procedimentos judiciais e extrajudiciais. Avaliação do dano corporal.', url: '/peritacion-medica' },
    ],
  };

  const t = texts[lang] || texts.es;
  const procs = procedimientos[lang] || procedimientos.es;

  return (
    <div className="min-h-screen bg-ivory">
      <Header />
      
      <section className="pt-12 pb-20 bg-cream">
        <div className="container mx-auto px-6 max-w-5xl text-center">
          <AnimatedSection animation="fadeUp">
            <p className="eyebrow mb-6">{t.eyebrow}</p>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-charcoal mb-6">
              {t.title1}<br /><span className="text-gold">{t.title2}</span>
            </h1>
            <div className="w-16 h-px bg-gold mx-auto mb-8"></div>
            <p className="text-text-secondary text-lg max-w-3xl mx-auto">{t.description}</p>
          </AnimatedSection>
        </div>
      </section>

      <section className="py-20 bg-ivory">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="grid gap-8">
            {procs.map((proc, index) => {
              const Icon = proc.icono;
              return (
                <AnimatedSection key={index} animation="fadeUp" delay={index * 0.1}>
                  <Link to={proc.url} className="group card-luxury p-8 flex flex-col md:flex-row gap-8 items-start">
                    <div className="w-16 h-16 border border-gold flex items-center justify-center flex-shrink-0 group-hover:bg-gold transition-colors">
                      <Icon className="text-gold group-hover:text-charcoal transition-colors" size={24} />
                    </div>
                    <div className="flex-1">
                      <h2 className="font-serif text-2xl text-charcoal mb-3 group-hover:text-gold transition-colors">{proc.titulo}</h2>
                      <p className="text-text-secondary leading-relaxed mb-4">{proc.descripcion}</p>
                      <span className="inline-flex items-center gap-2 text-gold text-sm tracking-wider uppercase group-hover:gap-3 transition-all">
                        {t.moreInfo} <ArrowRight size={14} />
                      </span>
                    </div>
                  </Link>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-charcoal">
        <div className="container mx-auto px-6 max-w-3xl text-center">
          <AnimatedSection animation="fadeUp">
            <p className="eyebrow text-gold mb-6">{t.ctaEyebrow}</p>
            <h2 className="font-serif text-3xl md:text-4xl text-ivory mb-6">{t.ctaTitle}</h2>
            <p className="text-gold-light/70 mb-10">{t.ctaDesc}</p>
            <Link to="/contacto" className="btn-gold inline-flex items-center gap-2 group">
              {t.ctaButton}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default CirugiaReparadora;
